import { Media, MediaType, formatDateToMMDDYYYY } from '@gripp/shared-logic';
import { ReactNode } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Colors } from '../../themes/colors';
import { Divider } from '../crop-circle/Components/Divider';
import { CategoryBadge } from './categoryBadge';
import { RenderMediaItem } from './renderMediaItem';
import { useTypedMediaItem } from './useTypedMediaItem';

type MediaListItemProps = {
  media: Media;
  invalidateQuery?: () => void;
  menu?: ReactNode; // Optional menu component for entity-specific actions
  useNewCropCircle?: boolean;
  onClickMedia?: (media: Media) => void;
};

/**
 * Shared component for displaying a media item in a list
 * Used by Product and Asset file lists
 * Handles images, documents, and links with proper click handlers
 */
export const MediaListItem = ({
  media,
  invalidateQuery,
  menu,
  useNewCropCircle,
  onClickMedia,
}: MediaListItemProps) => {
  const { typedMediaItem } = useTypedMediaItem(media);

  if (!useNewCropCircle) {
    return (
      <View style={styles.container}>
        <View style={styles.fileContainer}>
          {typedMediaItem && (
            <RenderMediaItem
              mediaItem={typedMediaItem}
              displayWidth={80}
              hasLightbox={media.type === MediaType.Image}
            />
          )}
          <View style={styles.textContainer}>
            <Text style={styles.name} numberOfLines={1} ellipsizeMode="tail">
              {media.name}
            </Text>
            <Text style={styles.subLabel}>
              {formatDateToMMDDYYYY(media.createdAt)} •{' '}
              {media.createdBy?.name.first} {media.createdBy?.name.last}
            </Text>
            {media.categories && media.categories.length > 0 && (
              <View style={styles.categoryContainer}>
                {media.categories.map((category) => (
                  <CategoryBadge key={category.id} category={category} />
                ))}
              </View>
            )}
          </View>
        </View>
        {menu}
      </View>
    );
  }

  return (
    <>
      <View style={styles.containerCropCircle}>
        <View style={styles.fileContainerCropCircle}>
          {typedMediaItem && (
            <RenderMediaItem
              mediaItem={typedMediaItem}
              displayWidth={88}
              hasLightbox={media.type === MediaType.Image}
              onPress={() => onClickMedia?.(media)}
            />
          )}
          <TouchableOpacity
            style={styles.textContainerCropCircle}
            onPress={() => onClickMedia?.(media)}
          >
            <Text
              style={styles.nameCropCircle}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {media.name}
            </Text>
            {media.categories && media.categories.length > 0 && (
              <View style={styles.categoryContainerCropCircle}>
                {media.categories.map((category) => (
                  <CategoryBadge key={category.id} category={category} />
                ))}
              </View>
            )}
          </TouchableOpacity>
        </View>
        {menu}
      </View>
      <Divider type="horizontal" />
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    justifyContent: 'space-between',
    flexDirection: 'row',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderColor: Colors.grayDivider,
    borderWidth: 1,
    backgroundColor: Colors.white,
    marginBottom: 12,
  },
  fileContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 15,
    flex: 1,
  },
  textContainer: {
    flex: 1,
    minWidth: 0,
    marginRight: 16,
  },
  name: {
    fontSize: 16,
    color: Colors.blackText,
    fontWeight: '600',
  },
  subLabel: {
    fontSize: 14,
    color: Colors.grayText,
    fontWeight: '500',
    marginTop: 12,
  },
  categoryContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 5,
    marginTop: 8,
  },
  containerCropCircle: {
    justifyContent: 'space-between',
    flexDirection: 'row',
    padding: 16,
    backgroundColor: Colors.white,
  },
  fileContainerCropCircle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 15,
    flex: 1,
  },
  textContainerCropCircle: {
    flex: 1,
    minWidth: 0,
    marginRight: 16,
    justifyContent: 'space-between',
    height: 88,
    flexDirection: 'column',
  },
  nameCropCircle: {
    fontSize: 16,
    color: Colors.blackText,
    fontWeight: '600',
  },
  categoryContainerCropCircle: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 5,
    marginTop: 8,
  },
});
